/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { ajax } from 'rxjs/ajax';
import { concat, of } from 'rxjs';
import { map, switchMap, filter, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';

import { STATUS } from 'sarsaparilla';

import {
    getFacilityUrl,
    getFacilityRatingReviewUrl,
    getFacilityImagesUrl,
} from '../api/endpoints';
import { averageMobileReviews, processMediaApiResponse } from '../utils';

import {
    FETCH_FACILITY_DATA,
    fetchFacilityDataSuccess,
    fetchFacilityDataError,
    setFetchFacilityDataStatus,
} from '../actions/facilityDataActions';

import {
    fetchReviewSummarySuccess,
    fetchReviewSummaryError,
    setFetchReviewSummaryStatus,
} from '../actions/reviewSummaryActions';

import {
    fetchFacilityImagesSuccess,
    fetchFacilityImagesError,
    setFetchFacilityImagesStatus,
} from '../actions/facilityImagesActions';

/**
 * Listen for the FETCH_FACILITY_DATA action to be dispatched
 * then get data for the facility page.
 *
 * Specifically:
 *   - facility data
 *   - rating-review summary for facility
 *   - images for facility
 *
 * The 3 api calls happen concurrently and finish independently.
 * If the facilityId is not included as the payload, switchMap logic is skipped
 */
export function fetchFacilityData(actions$) {
    return actions$.pipe(
        ofType(FETCH_FACILITY_DATA),
        filter(({ payload }) => !!payload),
        switchMap(({ payload: facilityId }) => {
            return concat(
                of(setFetchFacilityDataStatus(STATUS.PENDING)),
                of(setFetchReviewSummaryStatus(STATUS.PENDING)),
                of(setFetchFacilityImagesStatus(STATUS.PENDING)),
                ajax.getJSON(getFacilityUrl(facilityId)).pipe(
                    map((response) => fetchFacilityDataSuccess(response)),
                    catchError((error) => of(fetchFacilityDataError(error)))
                ),
                ajax.getJSON(getFacilityRatingReviewUrl(facilityId)).pipe(
                    map((response) => {
                        const rating = response?.average_rating;
                        const reviewCount = response?.number_of_ratings;
                        const cellAggregate = response?.aggregate_cell_coverage_ratings;
                        const mobileCoverageRating = cellAggregate
                            ? averageMobileReviews(cellAggregate)
                            : null;
                        const data = {
                            rating,
                            reviewCount,
                            mobileCoverageRating,
                            facilityId,
                        };
                        return fetchReviewSummarySuccess(data);
                    }),
                    catchError((error) => of(fetchReviewSummaryError(error)))
                ),
                ajax.getJSON(getFacilityImagesUrl(facilityId)).pipe(
                    map((response) => {
                        const data = processMediaApiResponse(response?.result);
                        return fetchFacilityImagesSuccess(data);
                    }),
                    catchError((error) => of(fetchFacilityImagesError(error)))
                )
            );
        })
    );
}
